/* Thin Cover: collection filtering and sorting (progressive enhancement for sections/main-collection.liquid).
   The section's GET forms work on their own. This adds: submit on change, region swaps through the
   Section Rendering API (?section_id=), browser history, the mobile filter drawer (a details element)
   and outside-click / Escape handling for the desktop filter dropdowns. No framework. */
(() => {
  if (customElements.get('collection-facets')) return;

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const announce = (text) => {
    const live = document.getElementById('a11y-live');
    if (!live || !text) return;
    live.textContent = '';
    setTimeout(() => { live.textContent = text; }, 60);
  };
  const visible = (el) => el.getClientRects().length > 0;

  class CollectionFacets extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.cache = new Map();
      this.seq = 0;
      this.currentUrl = window.location.pathname + window.location.search;
      this.header = document.querySelector('[data-header]');
      this.desktop = window.matchMedia('(min-width: 990px)');

      this.onChange = this.onChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onToggle = this.onToggle.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onDocClick = this.onDocClick.bind(this);
      this.onPopState = this.onPopState.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onBreakpoint = this.onBreakpoint.bind(this);

      this.addEventListener('change', this.onChange);
      this.addEventListener('submit', this.onSubmit);
      this.addEventListener('click', this.onClick);
      this.addEventListener('toggle', this.onToggle, true);
      this.addEventListener('keydown', this.onKeydown);
      document.addEventListener('click', this.onDocClick);
      window.addEventListener('popstate', this.onPopState);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.desktop.addEventListener?.('change', this.onBreakpoint);

      if (!(window.history.state && window.history.state.collectionFacets)) {
        window.history.replaceState({ ...(window.history.state || {}), collectionFacets: true }, '', window.location.href);
      }
      this.onScroll();
      if (window.Shopify && window.Shopify.designMode) this.revealAll();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
      window.removeEventListener('popstate', this.onPopState);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      this.desktop.removeEventListener?.('change', this.onBreakpoint);
      this.controller?.abort();
      if (this.classList.contains('is-drawer-open')) document.body.classList.remove('is-locked');
    }

    /* ---------- events ---------- */
    onChange(event) {
      const form = event.target.closest('form[data-facets-form]');
      if (!form) return;
      // Stop any request in flight right away, so a stale response can never undo this change.
      this.abort();
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.render(this.urlFor(form)), event.target.type === 'number' ? 400 : 180);
    }

    onSubmit(event) {
      const form = event.target.closest('form[data-facets-form]');
      if (!form) return;
      event.preventDefault();
      clearTimeout(this.timer);
      const fromDrawer = Boolean(form.closest('details.coll-drawer'));
      this.render(this.urlFor(form)).then(() => {
        if (!fromDrawer) return;
        this.closeDrawer(false);
        this.scrollToResults();
      });
    }

    onClick(event) {
      const dismiss = event.target.closest('[data-drawer-dismiss]');
      if (dismiss) {
        event.preventDefault();
        this.closeDrawer(true);
        return;
      }
      const link = event.target.closest('a[data-facet-link], .pagination a[href]');
      if (!link || !this.contains(link) || event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0) return;
      event.preventDefault();
      const isPage = Boolean(link.closest('.pagination'));
      clearTimeout(this.timer);
      this.render(link.href).then(() => { if (isPage) this.scrollToResults(); });
    }

    onToggle(event) {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement)) return;
      if (details.classList.contains('coll-drawer')) {
        this.classList.toggle('is-drawer-open', details.open);
        document.body.classList.toggle('is-locked', details.open);
        if (details.open && !details.classList.contains('coll-drawer--instant')) {
          requestAnimationFrame(() => details.querySelector('[data-drawer-dismiss]')?.focus());
        }
        if (!details.open) details.classList.remove('coll-drawer--instant');
        return;
      }
      if (details.open && details.closest('.coll-facets--bar')) {
        this.querySelectorAll('.coll-facets--bar details[open]').forEach((other) => { if (other !== details) other.open = false; });
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        if (this.querySelector('details.coll-drawer[open]')) {
          event.preventDefault();
          this.closeDrawer(true);
          return;
        }
        const open = event.target.closest?.('.coll-facets--bar details[open]') || this.querySelector('.coll-facets--bar details[open]');
        if (open) {
          event.preventDefault();
          open.open = false;
          open.querySelector('summary')?.focus();
        }
        return;
      }
      if (event.key === 'Tab') this.trapFocus(event);
    }

    onDocClick(event) {
      if (event.target.closest && event.target.closest('.coll-facets--bar .coll-facet')) return;
      this.querySelectorAll('.coll-facets--bar details[open]').forEach((details) => { details.open = false; });
    }

    onPopState() {
      const url = window.location.pathname + window.location.search;
      if (url === this.currentUrl) return;
      this.render(window.location.href, { push: false });
    }

    onBreakpoint() {
      if (this.desktop.matches) this.closeDrawer(false);
    }

    // Keep the sticky toolbar under the header while the header is on screen (works whether or not the header sticks).
    onScroll() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        const bottom = this.header ? Math.max(0, Math.round(this.header.getBoundingClientRect().bottom)) : 0;
        if (bottom !== this.stickyTop) {
          this.stickyTop = bottom;
          this.style.setProperty('--coll-sticky-top', `${bottom}px`);
        }
      });
    }

    /* ---------- drawer ---------- */
    closeDrawer(returnFocus) {
      const drawer = this.querySelector('details.coll-drawer');
      if (!drawer || !drawer.open) return;
      drawer.open = false;
      this.classList.remove('is-drawer-open');
      document.body.classList.remove('is-locked');
      if (returnFocus) drawer.querySelector('summary')?.focus();
    }

    trapFocus(event) {
      const panel = this.querySelector('details.coll-drawer[open] .coll-drawer__panel');
      if (!panel) return;
      const items = [...panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), summary')].filter(visible);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (!panel.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    scrollToResults() {
      const results = this.querySelector('[data-coll-region="results"]');
      if (!results) return;
      const top = results.getBoundingClientRect().top;
      if (top < 0 || top > window.innerHeight * 0.6) results.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
    }

    /* ---------- rendering ---------- */
    urlFor(form) {
      const url = new URL(form.getAttribute('action') || window.location.pathname, window.location.origin);
      const params = new URLSearchParams();
      new FormData(form).forEach((value, key) => {
        if (typeof value === 'string' && value.trim() !== '') params.append(key, value.trim());
      });
      url.search = params.toString();
      return url.toString();
    }

    abort() {
      this.seq += 1;
      this.controller?.abort();
      this.controller = null;
    }

    setBusy(on) {
      this.classList.toggle('is-busy', on);
      const results = this.querySelector('[data-coll-region="results"]');
      if (results) results.setAttribute('aria-busy', String(on));
    }

    async render(href, { push = true } = {}) {
      const url = new URL(href, window.location.origin);
      url.searchParams.delete('section_id');
      const pageUrl = url.pathname + url.search;
      if (push && pageUrl === this.currentUrl) {
        if (!this.controller) this.setBusy(false);
        return;
      }

      this.abort();
      const seq = this.seq;
      const controller = new AbortController();
      this.controller = controller;
      this.setBusy(true);
      try {
        let html = this.cache.get(pageUrl);
        if (!html) {
          const fetchUrl = new URL(url.toString());
          fetchUrl.searchParams.set('section_id', this.sectionId);
          const response = await fetch(fetchUrl.toString(), { signal: controller.signal, headers: { Accept: 'text/html' } });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          html = await response.text();
          if (this.cache.size > 24) this.cache.delete(this.cache.keys().next().value);
          this.cache.set(pageUrl, html);
        }
        if (seq !== this.seq) return;
        this.swap(html);
        this.currentUrl = pageUrl;
        if (push) window.history.pushState({ collectionFacets: true }, '', pageUrl);
      } catch (error) {
        if (error.name === 'AbortError' || seq !== this.seq) return;
        window.location.assign(pageUrl);
      } finally {
        if (seq === this.seq) {
          this.controller = null;
          this.setBusy(false);
        }
      }
    }

    swap(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const active = document.activeElement;
      const wasInside = Boolean(active && active !== document.body && this.contains(active));
      const activeId = wasInside ? active.id : '';
      const typing = wasInside && active.matches('input[type="number"], input[type="text"], input[type="search"]');
      const openIds = new Set([...this.querySelectorAll('details[open][data-details-id]')].map((d) => d.dataset.detailsId));
      const drawerScroll = this.querySelector('.coll-drawer__body')?.scrollTop || 0;

      this.querySelectorAll('[data-coll-region]').forEach((current) => {
        const next = doc.querySelector(`[data-coll-region="${current.dataset.collRegion}"]`);
        if (!next) return;
        if (typing && current.contains(active)) {
          // Never rebuild the form someone is typing in. Refresh its live labels only.
          current.querySelectorAll('[data-coll-live]').forEach((el) => {
            const twin = next.querySelector(`[data-coll-live="${el.dataset.collLive}"]`);
            if (twin) el.innerHTML = twin.innerHTML;
          });
          return;
        }
        current.replaceWith(document.importNode(next, true));
      });

      this.querySelectorAll('details[data-details-id]').forEach((details) => {
        const open = openIds.has(details.dataset.detailsId);
        if (open && details.classList.contains('coll-drawer')) details.classList.add('coll-drawer--instant');
        if (details.open !== open) details.open = open;
      });
      const drawerBody = this.querySelector('.coll-drawer__body');
      if (drawerBody) drawerBody.scrollTop = drawerScroll;

      this.revealAll();

      if (wasInside && !typing) {
        const again = activeId ? document.getElementById(activeId) : null;
        if (again && this.contains(again)) again.focus({ preventScroll: true });
        else if (!active.isConnected) this.querySelector('[data-coll-count]')?.focus({ preventScroll: true });
      }
      announce(this.querySelector('[data-coll-count]')?.textContent.trim());
    }

    // Cards arrive after theme.js set up its reveal observer, so show them straight away.
    revealAll() {
      this.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-in'));
    }
  }

  customElements.define('collection-facets', CollectionFacets);
})();
